import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useRequest } from "./request";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({ children }) {
//resolve this problem by pass user in local storage as default data
  let [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  let [role, setRole] = useState("Unknown");
  let [adminOn, setAdminOn] = useState(null);

  const req = useRequest();

  useEffect(() => {
    if (!user) {
      setRole("Unknown");
      setAdminOn(null);
      return;
    }

    if (user.roles?.includes("Company")) {
      setRole("Company");
      setAdminOn(user.company);
    } else if (user.roles?.includes("Site")) {
      setRole("Site");
      setAdminOn(user.site);
    } else if (user.roles?.includes("SubCompany")) {
      setRole("SubCompany");
      setAdminOn(user.subCompany);
    } else if (user.roles?.includes("Auditor")) {
      setRole("Auditor");
      setAdminOn(user.site);
    } else {
      setRole("Admin");
    }
  }, [user]);

  const saveTokens = useCallback((tokens, newUser) => {
    if (newUser) {
      localStorage.setItem("user", JSON.stringify(newUser));
    }
    if (tokens) {
      localStorage.setItem("tokens", JSON.stringify(tokens));
    }
  }, []);

  let signin = useCallback(
    (data) => {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await req(`Auth/login`, data);

          const loggedUser = res.data.user;
          const tokens = {
            accessToken: res.data.accessToken,
            refreshToken: res.data.refreshToken,
            expiration: res.data.expiration,
            issuedAt: res.data.issuedAt,
            localIssueAt: Date.now(),
          };

          setUser(loggedUser);
          saveTokens(tokens, loggedUser);

          resolve(res);
        } catch (error) {
          reject(error);
        }
      });
    },
    [req, saveTokens]
  );

  let signout = useCallback(() => {
    return new Promise((resolve) => {
      localStorage.removeItem("tokens");
      localStorage.removeItem("user");
      setUser(null);
      resolve();
    });
  }, []);

  let logout = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await req(`Auth/logout`, {}, {}, true);
        localStorage.removeItem("tokens");
        localStorage.removeItem("user");
        setUser(null);
        window.location.href = "/";
        resolve(res);
      } catch (error) {
        reject(error);
      }
    });
  }, [req]);

  let resetPass = useCallback(
    (data) => req(`Auth/password/reset`, data),
    [req]
  );

  let confirmresetPass = useCallback(
    (data) => req(`Auth/password/reset/confirm`, data),
    [req]
  );

  let changepass = useCallback(
    (data) => req(`Auth/password/change`, data, {}, true),
    [req]
  );

  let value = {
    user,
    role,
    adminOn,
    signin,
    signout,
    resetPass,
    confirmresetPass,
    changepass,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }) {
  const location = useLocation();
  let { user } = useAuth();

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  return children;
}
